import { googleLoginApi } from "@/features/auth/services/google-login-api";
import { googleRegisterApi } from "@/features/auth/services/google-register-api";
import { TGoogleRegisterState } from "@/features/auth/types/google-register";
import { AppRoutes } from "@/routes/config";
import { showError } from "@/utils/error";
import { logout } from "@/utils/logout";
import { createUrlWithParams } from "@/utils/url";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { FC, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// Register invites pass { email, resetToken } as Cognito's `state`, round-tripped back unmodified.
const parseRegisterState = (state: string | null): TGoogleRegisterState | null => {
  if (!state) return null;
  try {
    const parsed = JSON.parse(state);
    return parsed?.email && parsed?.resetToken ? parsed : null;
  } catch {
    return null;
  }
};

export const CognitoAuthCallbackPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isProcessing = useRef(false);

  useEffect(() => {
    const code = searchParams.get("code");
    const registerState = parseRegisterState(searchParams.get("state"));

    if (!code) {
      logout();
      navigate(AppRoutes.login, { replace: true });
      return;
    }

    // Prevent double execution in StrictMode
    if (isProcessing.current) return;
    isProcessing.current = true;

    const exchangeCode = async () => {
      if (registerState) {
        try {
          await googleRegisterApi({ code, token: registerState.resetToken });
          navigate(AppRoutes.registerSuccess, { replace: true, state: { email: registerState.email } });
        } catch (error) {
          showError(error);
          navigate(createUrlWithParams(AppRoutes.register, registerState), { replace: true });
        }
        return;
      }

      try {
        await googleLoginApi({ code });
        navigate(AppRoutes.home, { replace: true });
      } catch (error) {
        showError(error);
        logout();
        navigate(AppRoutes.login, { replace: true });
      }
    };

    exchangeCode();
  }, [searchParams, navigate]);

  return (
    <Stack alignItems="center" justifyContent="center" height="100vh" gap={2}>
      <CircularProgress />
      <Typography>ログイン中...</Typography>
    </Stack>
  );
};
